import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import { syncApi } from '../../api';
import type { QueueCountsResponse, QueueStatusResponse, SystemSettingRecord } from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Badge, formatDateTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import {
  IconPlus,
  IconSettings,
  IconShield,
  IconSync,
  IconTrash,
} from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import { usePaginatedList } from '../../hooks/usePagination';

export function AdminSystemPage() {
  const { maintenanceMode, refreshMaintenanceStatus } = useAuth();
  const { pushToast } = useToast();

  const [counts, setCounts] = useState<QueueCountsResponse | null>(null);
  const [status, setStatus] = useState<QueueStatusResponse | null>(null);
  const [settings, setSettings] = useState<SystemSettingRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [togglingMaintenance, setTogglingMaintenance] = useState<boolean>(false);
  const [controllingQueue, setControllingQueue] = useState<boolean>(false);

  // Upsert System Setting Modal
  const [settingModalOpen, setSettingModalOpen] = useState<boolean>(false);
  const [settingKey, setSettingKey] = useState<string>('');
  const [settingJsonValue, setSettingJsonValue] = useState<string>('{\n  "enabled": true\n}');
  const [savingSetting, setSavingSetting] = useState<boolean>(false);

  const loadSystemState = useCallback(async () => {
    setLoading(true);
    try {
      const [queueCounts, queueStatus, sysSettings] = await Promise.all([
        syncApi.getQueueCounts(),
        syncApi.getQueueStatus(),
        syncApi.listSystemSettings().catch(() => [] as SystemSettingRecord[]),
      ]);
      setCounts(queueCounts);
      setStatus(queueStatus);
      setSettings(Array.isArray(sysSettings) ? sysSettings : []);
      await refreshMaintenanceStatus();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to load system status',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast, refreshMaintenanceStatus]);

  useEffect(() => {
    void loadSystemState();
  }, [loadSystemState]);

  const maintenanceRecord = settings.find((s) => s.key === 'maintenance_mode');
  const isMaintenanceOn =
    maintenanceMode ||
    Boolean(
      maintenanceRecord &&
        (maintenanceRecord.maintenance_mode ||
          maintenanceRecord.value?.enabled === true ||
          maintenanceRecord.value?.maintenance_mode === true),
    );

  const handleToggleMaintenance = async (enable: boolean) => {
    setTogglingMaintenance(true);
    try {
      await syncApi.setMaintenanceMode(enable);
      pushToast({
        tone: enable ? 'warning' : 'success',
        title: enable ? 'Maintenance mode enabled' : 'Maintenance mode disabled',
        description: enable
          ? 'Scheduled background sync execution is now suspended globally.'
          : 'Normal sync execution has resumed.',
      });
      await loadSystemState();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to toggle maintenance mode',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setTogglingMaintenance(false);
    }
  };

  const handlePauseResumeQueue = async (pause: boolean) => {
    setControllingQueue(true);
    try {
      if (pause) {
        await syncApi.pauseQueue();
        pushToast({ tone: 'warning', title: 'Sync queue paused' });
      } else {
        await syncApi.resumeQueue();
        pushToast({ tone: 'success', title: 'Sync queue resumed' });
      }
      await loadSystemState();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Queue control failed',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setControllingQueue(false);
    }
  };

  const handleCleanQueue = async () => {
    if (!window.confirm('Clean completed and failed historical jobs from the sync queue?')) return;
    setControllingQueue(true);
    try {
      const res = await syncApi.cleanQueue();
      pushToast({
        tone: 'info',
        title: 'Queue cleaned',
        description: `Removed ${res.removed_jobs ?? 0} finished jobs.`,
      });
      await loadSystemState();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to clean queue',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setControllingQueue(false);
    }
  };

  const handleOpenEditSetting = (record?: SystemSettingRecord) => {
    if (record) {
      setSettingKey(record.key);
      setSettingJsonValue(JSON.stringify(record.value, null, 2));
    } else {
      setSettingKey('');
      setSettingJsonValue('{\n  "enabled": true\n}');
    }
    setSettingModalOpen(true);
  };

  const handleSaveSetting = async (e: FormEvent) => {
    e.preventDefault();
    if (!settingKey.trim()) return;
    let parsed: Record<string, unknown>;
    try {
      const raw = JSON.parse(settingJsonValue) as unknown;
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        parsed = raw as Record<string, unknown>;
      } else {
        parsed = { value: raw };
      }
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Invalid JSON value',
        description: err instanceof Error ? err.message : undefined,
      });
      return;
    }

    setSavingSetting(true);
    try {
      await syncApi.upsertSystemSetting(settingKey.trim(), parsed);
      pushToast({
        tone: 'success',
        title: 'System setting saved',
        description: `Updated "${settingKey.trim()}"`,
      });
      setSettingModalOpen(false);
      await loadSystemState();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to save system setting',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSavingSetting(false);
    }
  };

  const kindEntries = Object.entries(counts?.counts_by_kind ?? status?.counts_by_kind ?? {});
  const settingsPagination = usePaginatedList(settings, 10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            System Settings &amp; Maintenance Mode
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Control global platform maintenance state, inspect sync worker queues, and edit runtime configuration keys.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void loadSystemState()}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <IconSync className="h-3.5 w-3.5" />
          <span>Refresh Telemetry</span>
        </button>
      </div>

      {/* Maintenance Mode & Queue Control Banner Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Global Maintenance Switch */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                  isMaintenanceOn
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                <IconShield className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-slate-900">
                    Global Maintenance Mode
                  </h2>
                  <Badge variant={isMaintenanceOn ? 'warning' : 'success'} dot>
                    {isMaintenanceOn ? 'MAINTENANCE ACTIVE' : 'OPERATIONAL'}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Global synchronization worker execution state
                </p>
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-slate-600">
            Enabling maintenance mode prevents new scheduled sync runs from being dispatched
            during maintenance windows or upstream Procore outages.
          </p>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              disabled={togglingMaintenance || isMaintenanceOn}
              onClick={() => void handleToggleMaintenance(true)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold text-slate-950 shadow-2xs hover:bg-amber-400 disabled:opacity-40"
            >
              <span>Enable Maintenance Mode</span>
            </button>
            <button
              type="button"
              disabled={togglingMaintenance || !isMaintenanceOn}
              onClick={() => void handleToggleMaintenance(false)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-2xs hover:bg-emerald-500 disabled:opacity-40"
            >
              <span>Resume Normal Operation</span>
            </button>
          </div>
        </div>

        {/* Sync Queue Engine Control */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <IconSettings className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-slate-900">
                    Sync Queue Worker
                  </h2>
                  <Badge variant={status?.paused ? 'warning' : 'success'} dot>
                    {status?.paused ? 'PAUSED' : 'RUNNING'}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Background synchronization job queue status
                </p>
              </div>
            </div>
          </div>

          {/* Job Counters */}
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] font-medium text-slate-500">Total Pending</div>
              <div className="mt-0.5 text-lg font-bold text-slate-900">
                {counts?.total_pending ?? status?.pending ?? 0}
              </div>
            </div>
            <div className="rounded-xl border border-indigo-200/70 bg-indigo-50/50 p-3">
              <div className="text-[11px] font-medium text-indigo-600">Active Workers</div>
              <div className="mt-0.5 text-lg font-bold text-indigo-700">
                {counts?.active_workers ?? status?.active_workers ?? 0}
              </div>
            </div>
            <div className="rounded-xl border border-emerald-200/70 bg-emerald-50/50 p-3">
              <div className="text-[11px] font-medium text-emerald-600">Job Kinds</div>
              <div className="mt-0.5 text-lg font-bold text-emerald-700">{kindEntries.length}</div>
            </div>
          </div>

          {kindEntries.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {kindEntries.map(([kind, num]) => (
                <Badge key={kind} variant="neutral">
                  {kind}: {num}
                </Badge>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2.5">
            {status?.paused ? (
              <button
                type="button"
                disabled={controllingQueue}
                onClick={() => void handlePauseResumeQueue(false)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                <span>Resume Queue</span>
              </button>
            ) : (
              <button
                type="button"
                disabled={controllingQueue}
                onClick={() => void handlePauseResumeQueue(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              >
                <span>Pause Queue</span>
              </button>
            )}
            <button
              type="button"
              disabled={controllingQueue}
              onClick={() => void handleCleanQueue()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <IconTrash className="h-3.5 w-3.5" />
              <span>Prune Completed/Failed Jobs</span>
            </button>
          </div>
        </div>
      </div>

      {/* System Settings Key-Value Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xs">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <IconSettings className="h-5 w-5 text-amber-500" />
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Runtime System Settings
              </h2>
              <p className="text-xs text-slate-500">
                Platform configuration flags and runtime overrides.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleOpenEditSetting()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-3.5 py-2 text-xs font-semibold text-slate-950 hover:bg-amber-400"
          >
            <IconPlus className="h-3.5 w-3.5" />
            <span>Upsert Setting Key</span>
          </button>
        </div>

        <div className="table-scroll-container">
          <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
            <thead className="bg-slate-50 font-semibold uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-6 py-3">Setting Key</th>
                <th className="px-6 py-3">JSON Value</th>
                <th className="px-6 py-3">Last Updated</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    Loading system settings…
                  </td>
                </tr>
              ) : settingsPagination.pageItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    No custom keys in <code className="font-mono">system_settings</code> yet. Click &ldquo;Upsert Setting Key&rdquo; to add one.
                  </td>
                </tr>
              ) : (
                settingsPagination.pageItems.map((item) => (
                  <tr key={item.key} className="hover:bg-slate-50">
                    <td className="px-6 py-3.5 font-mono text-xs font-semibold text-slate-900">
                      {item.key}
                    </td>
                    <td className="px-6 py-3.5">
                      <pre className="max-w-xl overflow-x-auto rounded bg-slate-100 px-2.5 py-1.5 font-mono text-xs text-slate-800">
                        {JSON.stringify(item.value)}
                      </pre>
                    </td>
                    <td className="px-6 py-3.5 text-slate-500">{formatDateTime(item.updated_at)}</td>
                    <td className="px-6 py-3.5 text-right">
                      <button
                        type="button"
                        onClick={() => handleOpenEditSetting(item)}
                        className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Edit JSON
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar pagination={settingsPagination} itemLabel="settings" />
      </div>

      {/* Upsert Setting Dialog */}
      <Dialog
        open={settingModalOpen}
        onClose={() => setSettingModalOpen(false)}
        title="Upsert System Setting"
        subtitle="Update a runtime configuration key and JSON value."
      >
        <form onSubmit={handleSaveSetting} className="space-y-4">
          <div>
            <label
              htmlFor="sys-setting-key"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >
              Setting Key *
            </label>
            <input
              id="sys-setting-key"
              type="text"
              required
              value={settingKey}
              onChange={(e) => setSettingKey(e.target.value)}
              placeholder="e.g. maintenance_mode or procore_rate_limit"
              className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900"
            />
          </div>

          <div>
            <label
              htmlFor="sys-setting-json"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >
              JSON Payload *
            </label>
            <textarea
              id="sys-setting-json"
              rows={6}
              required
              value={settingJsonValue}
              onChange={(e) => setSettingJsonValue(e.target.value)}
              className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-900"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setSettingModalOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingSetting || !settingKey.trim()}
              className="rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
            >
              {savingSetting ? 'Saving…' : 'Save Setting'}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
